import React from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import { useLanguage } from '../contexts/LanguageProvider';
import HomeIcon from './icons/HomeIcon';
import SearchIcon from './icons/SearchIcon';
import UsersIcon from './icons/UsersIcon';
import BriefcaseIcon from './icons/BriefcaseIcon';
import SparklesIcon from './icons/SparklesIcon';
import CloseIcon from './icons/CloseIcon';
import VolumeUpIcon from './icons/VolumeUpIcon';
import LogoutIcon from './icons/LogoutIcon';
import PhotoIcon from './icons/PhotoIcon';
import ListBulletIcon from './icons/ListBulletIcon';
import SettingsIcon from './icons/SettingsIcon';
import MicrophoneIcon from './icons/MicrophoneIcon';
import VerifiedBadge from './VerifiedBadge';
import type { LanguageCode } from '../i18n/translations';

const MobileMenu: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { isMobileMenuOpen, setIsMobileMenuOpen, view, handleNavigation } = useNavigation();
  const { opportunities } = useOpportunities();
  const { language, setLanguage, t } = useLanguage();
  
  const onClose = () => setIsMobileMenuOpen(false);
  
  const handleNavAndClose = (navFunc: () => void) => {
    navFunc();
    onClose();
  }

  if (!currentUser) {
    return null;
  }

  const hasPostedOpportunities = opportunities.some(opp => opp.author.handle === currentUser.handle);
  const tier = currentUser.subscriptionTier || 'basic';
  const isPro = tier.includes('_pro');
  const isProPlus = tier.includes('_pro_plus');
  const canAccessLineups = ['talent_pro', 'talent_pro_plus', 'business_pro', 'business_pro_plus'].includes(tier);
  
  const isSeed = currentUser.isSeed;
  const isAdmin = !!currentUser?.isAdmin;
  const showProDashboard = isProPlus || isSeed || isAdmin;

  const languages: { code: LanguageCode; label: string }[] = [
    { code: 'en', label: 'EN' },
    { code: 'it', label: 'IT' },
  ];

  return (
    <div 
      className={`fixed inset-0 z-[100] transition-opacity ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} font-urbanist`}
      aria-hidden={!isMobileMenuOpen}
      role="dialog"
      aria-modal="true"
    >
      <div 
        onClick={onClose} 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        tabIndex={-1}
        aria-hidden="true"
      ></div>
      <div className={`relative flex flex-col w-4/5 max-sm:w-[85%] max-w-sm h-full bg-brand-surface shadow-2xl transition-transform transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <div className="flex items-center space-x-2">
            <h2 className="text-xl font-black text-white uppercase tracking-tighter">Navigation</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-800 transition-colors" aria-label="Close menu">
            <CloseIcon className="h-6 w-6 text-white" />
          </button>
        </div>
        
        <div className="flex-grow overflow-y-auto p-5 space-y-8">
            <div className="flex items-center space-x-4 bg-gray-800/40 p-4 rounded-2xl border border-gray-800">
              <div className="relative">
                <img src={currentUser.avatarUrl} alt={currentUser.name} className="h-16 w-16 rounded-full border-2 border-brand-orange" />
                {isProPlus ? <VerifiedBadge className="absolute -bottom-1 -right-1 h-5 w-5 text-blue-500 bg-brand-surface rounded-full p-0.5" /> : isPro ? <VerifiedBadge className="absolute -bottom-1 -right-1 h-5 w-5 text-green-400 bg-brand-surface rounded-full p-0.5" /> : null}
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-black text-white truncate uppercase tracking-tighter leading-tight">{currentUser.name}</h2>
                <p className="text-xs text-brand-muted font-mono uppercase">@{currentUser.handle}</p>
                <p className="mt-1 text-[10px] text-brand-orange font-bold uppercase tracking-widest">{currentUser.subscriptionTier?.replace(/_/g, ' ')}</p>
              </div>
            </div>

            <div className="space-y-2">
                <label className="text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em] px-2">{t('LANGUAGE')}</label>
                <div className="flex flex-wrap gap-2">
                    {languages.map(lang => (
                        <button
                            key={lang.code}
                            onClick={() => setLanguage(lang.code)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${language === lang.code ? 'bg-brand-orange/20 border-brand-orange text-white' : 'bg-gray-800 border-gray-700 text-brand-muted'}`}
                        >
                            {lang.label}
                        </button>
                    ))}
                </div>
            </div>

            <button
              onClick={() => handleNavAndClose(() => handleNavigation('createOpportunity'))}
              className="w-full bg-brand-orange text-white font-bold py-4 px-4 rounded-full hover:bg-orange-600 transition-all text-sm uppercase tracking-widest shadow-lg shadow-brand-orange/20"
            >
              {t('NAV_POST_OPP')}
            </button>

            <nav>
              <ul className="space-y-1">
                <NavItem icon={<HomeIcon className="h-5 w-5" />} label={t('NAV_HOME')} active={view === 'feed'} onClick={() => handleNavAndClose(() => handleNavigation('feed'))} />
                <NavItem icon={<SearchIcon className="h-5 w-5" />} label="Search" active={view === 'search'} onClick={() => handleNavAndClose(() => handleNavigation('search'))} />
                <NavItem icon={<MicrophoneIcon className="h-5 w-5" />} label="Sessions" active={view === 'sessions'} onClick={() => handleNavAndClose(() => handleNavigation('sessions'))} />
                <NavItem icon={<SparklesIcon className="h-5 w-5" />} label={t('NAV_FOR_YOU')} active={view === 'recommended'} onClick={() => handleNavAndClose(() => handleNavigation('recommended'))} />

                {isAdmin && (
                    <NavItem icon={<SettingsIcon className="h-5 w-5 text-red-400" />} label="Admin Dashboard" active={view === 'adminDashboard'} onClick={() => handleNavAndClose(() => handleNavigation('adminDashboard'))} />
                )}

                {showProDashboard && (
                    <NavItem icon={<UsersIcon className="h-5 w-5 text-blue-400" />} label={isSeed ? "Founder's Hub" : "Pro Analytics"} active={view === 'proDashboard'} onClick={() => handleNavAndClose(() => handleNavigation('proDashboard'))} />
                )}

                <NavItem icon={<UsersIcon className="h-5 w-5" />} label={t('NAV_MY_BANDS')} active={view === 'myProjects'} onClick={() => handleNavAndClose(() => handleNavigation('myProjects'))} />

                <NavItem icon={<UsersIcon className="h-5 w-5" />} label={t('NAV_NETWORK')} active={view === 'network'} onClick={() => handleNavAndClose(() => handleNavigation('network'))} />
                <NavItem icon={<BriefcaseIcon className="h-5 w-5" />} label={t('NAV_ACTIVITY')} active={view === 'myActivity'} onClick={() => handleNavAndClose(() => handleNavigation('myActivity'))} />
                
                {hasPostedOpportunities && (
                    <NavItem icon={<BriefcaseIcon className="h-5 w-5" />} label={t('NAV_MANAGE_APPLICANTS')} active={view === 'manageApplicants'} onClick={() => handleNavAndClose(() => handleNavigation('manageApplicants'))} />
                )}

                {canAccessLineups && (
                  <>
                    <div className="pt-4 pb-2 px-3 text-[10px] font-extrabold text-brand-muted uppercase tracking-[0.2em]">Management</div>
                    <NavItem icon={<ListBulletIcon className="h-5 w-5" />} label={t('NAV_MY_LINEUPS')} active={view === 'myLineups'} onClick={() => handleNavAndClose(() => handleNavigation('myLineups'))} />
                    <NavItem icon={<PhotoIcon className="h-5 w-5" />} label={t('NAV_MY_FLYERS')} active={view === 'myFlyers'} onClick={() => handleNavAndClose(() => handleNavigation('myFlyers'))} />
                  </>
                )}

                <div className="pt-4 mt-4 border-t border-gray-800">
                    <NavItem icon={<VolumeUpIcon className="h-5 w-5" />} label={t('NAV_PUMP_UP')} active={view === 'pumpUpTheVolume'} onClick={() => handleNavAndClose(() => handleNavigation('pumpUpTheVolume'))} />
                </div>
              </ul>
            </nav>
        </div>
         <div className="p-5 border-t border-gray-800 bg-gray-900/20">
            <NavItem icon={<LogoutIcon className="h-5 w-5" />} label={t('LOGOUT')} onClick={logout} />
        </div>
      </div>
    </div>
  );
};

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
  badgeCount?: number;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, active, onClick, badgeCount }) => (
  <li>
    <button
      onClick={onClick}
      className={`flex items-center justify-between p-3 w-full text-left rounded-xl transition-all ${
        active
          ? 'text-white bg-brand-orange/20 font-bold'
          : 'text-brand-muted hover:text-white hover:bg-gray-800'
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className={`${active ? 'text-brand-orange' : ''}`}>{icon}</div>
        <span className="text-sm">{label}</span>
      </div>
      {badgeCount && badgeCount > 0 && (
        <span className="bg-brand-orange text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg">
          {badgeCount}
        </span>
      )}
    </button>
  </li>
);

export default MobileMenu;